<template>
  <div>
    <NavBar />
    <div class="sign-up">
      <div id="left-col">
        <p style="margin-bottom: 1.5rem; font-weight: 600; font-size: 1.25rem;">Genius College Advice on Demand!</p>

        <div class="form-group">
          <div class="form-label">I am a:</div>
          <select v-model="type" @change="resetForm" class="form-select" required>
            <option disabled value="">Select Type</option>
            <option value="student">Student</option>
            <option value="counselor">Counselor</option>
          </select>
          <span class="error-text" v-if="formErrors.type">{{ formErrors.type }}</span>
        </div>

        <!-- Student fields -->
        <div v-if="type === 'student'" class="form-group">
          <div class="form-label">Select your school:</div>
          <select v-model="school" class="form-select" required>
            <option value="" disabled selected>Select School...</option>
            <option v-for="college in colleges" :key="college.id" :value="college.title">
              {{ college.title }}
            </option>
          </select>
          <span class="error-text" v-if="formErrors.school">{{ formErrors.school }}</span>
        </div>
        
        <!-- Counselor fields -->
        <div v-if="type === 'counselor'" class="form-group">
          <div class="form-label">Hourly Rate:</div>
          <input type="number" step="0.01" placeholder="Hourly Rate (ex 40.00)" v-model="hourly_rate" required />
          <span class="error-text" v-if="formErrors.hourly_rate">{{ formErrors.hourly_rate }}</span>
          
          <div class="form-label">Select your university:</div>
          <select v-model="university" class="form-select" required>
            <option value="" disabled selected>Select University...</option>
            <option v-for="college in colleges" :key="college.id" :value="college.title">
              {{ college.title }}
            </option>
          </select>
          <span class="error-text" v-if="formErrors.university">{{ formErrors.university }}</span>
        </div>

        <div class="google-signin-wrapper">
          <div id="google-sign-in"></div>
        </div>
        
        <form @submit.prevent="validateAndSubmit" class="signup-form">
          <div class="form-group">
            <div class="form-label">First Name:</div>
            <input type="text" placeholder="First Name" v-model="first_name" required />
            <span class="error-text" v-if="formErrors.first_name">{{ formErrors.first_name }}</span>
          </div>

          <div class="form-group">
            <div class="form-label">Last Name:</div>
            <input type="text" placeholder="Last Name" v-model="last_name" required />
            <span class="error-text" v-if="formErrors.last_name">{{ formErrors.last_name }}</span>
          </div>

          <div class="form-group">
            <div class="form-label">Email:</div>
            <input type="email" placeholder="Email" v-model="email" required />
            <span class="error-text" v-if="formErrors.email">{{ formErrors.email }}</span>
          </div>

          <div class="form-group">
            <div class="form-label">Password:</div>
            <input type="password" placeholder="Password" v-model="password" required />
            <span class="error-text" v-if="formErrors.password">{{ formErrors.password }}</span>
          </div>

          <div class="form-group">
            <div class="form-label">Confirm Password:</div>
            <input type="password" placeholder="Confirm Password" v-model="confirm_password" required />
            <span class="error-text" v-if="formErrors.confirm_password">{{ formErrors.confirm_password }}</span>
          </div>

          <button id="submit" type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? 'Creating Account...' : 'Submit' }}
          </button>
        </form>
        
        <p v-if="error" class="error-message">{{ error }}</p>
        <p v-if="success" class="success-message">Account created successfully!</p>
      </div>
      <div id="right-col">
        <img src="../../assets/images/sign-up.png" alt="Sign Up" />
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';
import NavBar from './../../components/NavBar.vue';

const API_BASE = process.env.VUE_APP_API_BASE_URL || '/api';
const GOOGLE_CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID || '410290594871-9ieo9jahuq12um05igdpah1kv520crcr.apps.googleusercontent.com';

export default {
  name: 'SignUpView',
  components: {
    NavBar,
  },
  setup() {
    const router = useRouter();
    return { router };
  },
  data() {
    return {
      colleges: [],
      first_name: '',
      last_name: '',
      email: '',
      type: '',
      hourly_rate: 0.0,
      university: '',
      school: '',
      password: '',
      confirm_password: '',
      error: '',
      success: false,
      isSubmitting: false,
      formErrors: {
        type: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: '',
        school: '',
        hourly_rate: '',
        university: ''
      }
    };
  },
  methods: {
    // Called by Google Identity Services
    async handleCredentialResponse({ credential }) {
      try {
        // send the Google ID token to your backend to create/login the user
        const res = await fetch(`${API_BASE}/auth/google/one-tap`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ credential, type: this.type, school: this.school, university: this.university, hourly_rate: this.hourly_rate })
        });
        if (!res.ok) throw new Error('Google sign-in failed');
        this.router.push('/');
      } catch (e) {
        this.error = e.message || 'Google sign-in failed';
        console.error(e);
      }
    },

    resetForm() {
      this.school = '';
      this.hourly_rate = '';
      this.university = '';
      this.formErrors = {};
    },

    validateForm() {
      let isValid = true;
      this.formErrors = {};

      if (!this.type) {
        this.formErrors.type = 'Please select whether you are a student or counselor';
        isValid = false;
      }

      if (!this.first_name.trim()) {
        this.formErrors.first_name = 'First name is required';
        isValid = false;
      }

      if (!this.last_name.trim()) {
        this.formErrors.last_name = 'Last name is required';
        isValid = false;
      }

      if (!this.email) {
        this.formErrors.email = 'Email is required';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
        this.formErrors.email = 'Please enter a valid email address';
        isValid = false;
      }

      if (!this.password) {
        this.formErrors.password = 'Password is required';
        isValid = false;
      } else if (this.password.length < 8) {
        this.formErrors.password = 'Password must be at least 8 characters long';
        isValid = false;
      }

      if (this.password !== this.confirm_password) {
        this.formErrors.confirm_password = 'Passwords do not match';
        isValid = false;
      }

      if (this.type === 'student' && !this.school) {
        this.formErrors.school = 'Please select your school';
        isValid = false;
      }

      if (this.type === 'counselor') {
        if (!this.hourly_rate || this.hourly_rate <= 0) {
          this.formErrors.hourly_rate = 'Please enter a valid hourly rate';
          isValid = false;
        }
        if (!this.university) {
          this.formErrors.university = 'Please select your university';
          isValid = false;
        }
      }

      return isValid;
    },

    async validateAndSubmit() {
      if (!this.validateForm()) return;

      this.isSubmitting = true;
      this.error = '';
      this.success = false;

      const userData = {
        first_name: this.first_name.trim(),
        last_name: this.last_name.trim(),
        type: this.type,
        email: this.email,
        password: this.password,
        hourly_rate: this.type === 'counselor' ? this.hourly_rate : null,
        university: this.type === 'counselor' ? this.university : null,
        school: this.type === 'student' ? this.school : null
      };

      try {
        const response = await fetch(`${API_BASE}/add-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(userData),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to create account');
        }

        this.success = true;
        setTimeout(() => { window.location.href = '/'; }, 1000);
      } catch (error) {
        this.error = error.message;
      } finally {
        this.isSubmitting = false;
      }
    },

    loadGoogleScript() {
      const existingScript = document.getElementById('google-oauth');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.id = 'google-oauth';
        script.onload = this.renderGoogleButton;
        document.head.appendChild(script);
      } else {
        this.renderGoogleButton();
      }
    },

    renderGoogleButton() {
      if (window.google?.accounts.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: this.handleCredentialResponse,
        });

        window.google.accounts.id.renderButton(
          document.getElementById('google-sign-in'),
          {
            theme: 'outline',
            size: 'large',
            width: '100%',
          }
        );
      }
    },

    async fetchColleges() {
      try {
        const response = await fetch(`${API_BASE}/colleges`, { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to fetch colleges');
        const data = await response.json();
        this.colleges = data.colleges.sort((a, b) => a.title.localeCompare(b.title));
      } catch (error) {
        console.error('Error loading colleges:', error);
        this.error = 'Failed to load colleges. Please try again.';
      }
    }
  },
  mounted() {
    this.loadGoogleScript();
    this.fetchColleges(); // moved college-fetch logic into method
  },
};
</script>

<style scoped>
.sign-up {
  display: flex;
  justify-content: space-between;
  min-height: calc(100vh - 80px); /* Subtract navbar height */
  background-color: #f5f5f5;
  margin-top: 80px; /* Add margin for navbar */
  padding: 20px 0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
  text-align: left;
  background-color: #f5f5f5;
}

.error-text {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
  text-align: left;
}

.error-message {
  color: #dc3545;
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 0.25rem;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
}

.success-message {
  color: #28a745;
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 0.25rem;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
}

input, select {
  width: 100%;
  padding: 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  font-size: 1rem;
  background-color: white;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

select {
  margin-top: 0.25rem;
  margin-bottom: 1rem;

  padding: 0.75rem 1rem;
  font-size: 1rem;
  line-height: 1.5;
  min-height: 48px;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 6px;
}

input:focus, select:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

#submit {
  background-color: #3867ae;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  cursor: pointer;
  width: 100%;
  margin-top: 1rem;
}

#submit:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

#submit:hover:not(:disabled) {
  background-color: #2d5392;
}

#right-col img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

#left-col {
  width: 40%;
  padding: 2rem;
}

#right-col {
  width: 60%;
}

@media (max-width: 768px) {
  .sign-up {
    flex-direction: column;
  }
  
  #left-col,
  #right-col {
    width: 100%;
    padding: 1rem;
  }
}

.google-signin-wrapper {
  margin: 1.5rem 0;
  display: flex;
  justify-content: center;
}
</style>